import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getUserRoleLabel, BlocklistMatchType } from '../types';
import ConfirmModal from '../components/ConfirmModal';
import { User, Lock, CheckCircle, ShieldOff, Plus, Trash2, Bell, BellOff, Mail, MailX } from 'lucide-react';
import { isPushSupported, isPushSubscribed, subscribeToPush, unsubscribeFromPush } from '../services/pushNotifications';

export default function ProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuthStore();

  const { data } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
  });

  const profile = data?.data;

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setPhone(profile.phone || '');
    }
  }, [profile?.id]);

  const profileMutation = useMutation({
    mutationFn: () => api.updateProfile({ name, phone }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setSuccessMsg(t('profile.updated'));
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const passwordMutation = useMutation({
    mutationFn: () => api.updateProfile({ current_password: currentPassword, new_password: newPassword }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMsg(t('profile.passwordUpdated'));
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert(t('profile.passwordMismatch'));
      return;
    }
    passwordMutation.mutate();
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="page-title">{t('profile.title')}</h1>

      {successMsg && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-success-50 border-r-4 border-success-600">
          <CheckCircle className="text-success-600" size={20} />
          <p className="text-green-800">{successMsg}</p>
        </div>
      )}

      {/* Profile Info */}
      <div className="card">
        <div className="flex items-center gap-4 mb-6">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-card"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary-700, #006d77), var(--brand-primary-800, #00535b))' }}
          >
            <User className="text-white" size={32} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-surface-900 tracking-tight font-headline">{profile?.name}</h2>
            <p className="text-surface-500">{profile?.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 rounded text-xs bg-primary-100 text-primary-800">
                {authUser && getUserRoleLabel(authUser.role, t)}
              </span>
              <span className="text-xs text-surface-400">{(profile as any)?.organization_name}</span>
            </div>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); profileMutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('common.name')}</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
          </div>
          <div>
            <label className="label">{t('common.phone')}</label>
            <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label className="label">{t('common.email')}</label>
            <input className="input bg-surface-50" value={profile?.email || ''} disabled />
            <p className="text-xs text-surface-400 mt-1">{t('profile.emailNote')}</p>
          </div>

          {profileMutation.error && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">
              {(profileMutation.error as any)?.error?.message || t('profile.updateError')}
            </div>
          )}

          <button type="submit" disabled={profileMutation.isPending} className="btn btn-primary">
            {profileMutation.isPending ? t('common.updating') : t('profile.updateProfile')}
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="text-surface-600" size={24} />
          <h2 className="text-xl font-bold text-surface-900 tracking-tight font-headline">{t('profile.changePassword')}</h2>
        </div>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="label">{t('profile.currentPassword')}</label>
            <input type="password" className="input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
          </div>
          <div>
            <label className="label">{t('profile.newPassword')}</label>
            <input type="password" className="input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
          </div>
          <div>
            <label className="label">{t('profile.confirmPassword')}</label>
            <input type="password" className="input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} />
          </div>

          {passwordMutation.error && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">
              {(passwordMutation.error as any)?.error?.message || t('profile.changePasswordError')}
            </div>
          )}

          <button type="submit" disabled={passwordMutation.isPending} className="btn bg-surface-800 text-white hover:bg-surface-900">
            {passwordMutation.isPending ? t('common.updating') : t('profile.changePasswordBtn')}
          </button>
        </form>
      </div>

      {/* Email Notifications */}
      <EmailNotificationSection />

      {/* Push Notifications */}
      <PushNotificationSection />

      {/* Personal Blocklist */}
      <PersonalBlocklistSection />
    </div>
  );
}

function EmailNotificationSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
  });

  const profile = data?.data;
  const emailEnabled = profile?.email_notifications ?? true;

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => api.updateProfile({ email_notifications: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {emailEnabled ? <Mail className="text-primary-600" size={24} /> : <MailX className="text-surface-400" size={24} />}
          <div>
            <h2 className="text-lg font-bold text-surface-900">{t('profile.emailNotifications')}</h2>
            <p className="text-sm text-surface-500">
              {emailEnabled ? t('profile.emailNotificationsOn') : t('profile.emailNotificationsOff')}
            </p>
          </div>
        </div>
        <button
          onClick={() => toggleMutation.mutate(!emailEnabled)}
          disabled={toggleMutation.isPending}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${emailEnabled ? 'bg-primary-600' : 'bg-surface-300'}`}
          aria-label={t('profile.emailNotifications')}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-surface-0 transition-transform ${emailEnabled ? 'translate-x-1' : 'translate-x-6'}`} />
        </button>
      </div>
    </div>
  );
}

function PushNotificationSection() {
  const { t } = useTranslation();
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sup = isPushSupported();
    setSupported(sup);
    if (sup) {
      isPushSubscribed().then(setSubscribed);
    }
  }, []);

  const toggle = async () => {
    setLoading(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
      } else {
        const ok = await subscribeToPush();
        setSubscribed(ok);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {subscribed ? <Bell className="text-primary-600" size={24} /> : <BellOff className="text-surface-400" size={24} />}
          <div>
            <h2 className="text-lg font-bold text-surface-900">{t('push.enableNotifications').split(' ').slice(0, 3).join(' ')}</h2>
            <p className="text-sm text-surface-500">
              {subscribed ? t('push.subscribed') : t('push.enableNotifications')}
            </p>
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={loading}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${subscribed ? 'bg-primary-600' : 'bg-surface-300'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-surface-0 transition-transform ${subscribed ? 'translate-x-1' : 'translate-x-6'}`} />
        </button>
      </div>
    </div>
  );
}

function PersonalBlocklistSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [fieldName, setFieldName] = useState('phone');
  const [matchValue, setMatchValue] = useState('');
  const [matchType, setMatchType] = useState<BlocklistMatchType>('exact');
  const [reason, setReason] = useState('');

  const { data } = useQuery({
    queryKey: ['personal-blocklist'],
    queryFn: () => api.getPersonalBlocklistRules(),
  });

  const rules = data?.data || [];

  const createMutation = useMutation({
    mutationFn: () => api.createPersonalBlocklistRule({
      field_name: fieldName,
      match_value: matchValue,
      match_type: matchType,
      reason: reason || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-blocklist'] });
      setShowAdd(false);
      setMatchValue('');
      setReason('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deletePersonalBlocklistRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-blocklist'] });
      setDeleteId(null);
    },
  });

  const matchTypeLabels: Record<BlocklistMatchType, string> = {
    exact: t('blocklist.matchExact'),
    normalized_phone: t('blocklist.matchPhone'),
    domain: t('blocklist.matchDomain'),
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldOff className="text-surface-600" size={24} />
          <h2 className="text-xl font-bold text-surface-900 tracking-tight font-headline">{t('blocklist.personalTitle')}</h2>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="btn btn-primary text-sm flex items-center gap-1">
          <Plus size={14} />
          {t('blocklist.addRule')}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="mb-4 p-4 bg-surface-50 rounded-xl space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label text-xs">{t('blocklist.fieldName')}</label>
              <select className="input text-sm" value={fieldName} onChange={(e) => setFieldName(e.target.value)}>
                <option value="phone">phone</option>
                <option value="email">email</option>
                <option value="domain">domain</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">{t('blocklist.matchValue')}</label>
              <input className="input text-sm" value={matchValue} onChange={(e) => setMatchValue(e.target.value)} placeholder={t('blocklist.valuePlaceholder')} dir="ltr" required />
            </div>
            <div>
              <label className="label text-xs">{t('blocklist.matchType')}</label>
              <select className="input text-sm" value={matchType} onChange={(e) => setMatchType(e.target.value as BlocklistMatchType)}>
                <option value="exact">{matchTypeLabels.exact}</option>
                <option value="normalized_phone">{matchTypeLabels.normalized_phone}</option>
                <option value="domain">{matchTypeLabels.domain}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label text-xs">{t('blocklist.reason')}</label>
            <input className="input text-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('blocklist.reasonPlaceholder')} />
          </div>
          {createMutation.error && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded text-sm">
              {(createMutation.error as any)?.error?.message || t('blocklist.createError')}
            </div>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={createMutation.isPending} className="btn btn-primary text-sm">
              {createMutation.isPending ? t('common.creating') : t('blocklist.addRule')}
            </button>
            <button type="button" onClick={() => setShowAdd(false)} className="btn bg-surface-200 text-surface-700 text-sm">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {rules.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-text">{t('blocklist.noPersonalRules')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <div key={rule.id} className="flex items-center justify-between bg-surface-50 rounded-xl p-4 border border-surface-400/10">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-surface-600">{rule.field_name}</span>
                <span className="font-mono text-sm" dir="ltr">{rule.match_value}</span>
                <span className="text-xs bg-surface-200 text-surface-600 px-2 py-0.5 rounded">{matchTypeLabels[rule.match_type]}</span>
                {rule.reason && <span className="text-xs text-surface-500">{rule.reason}</span>}
              </div>
              <button onClick={() => setDeleteId(rule.id)} className="text-surface-400 hover:text-danger-500">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {deleteId && (
        <ConfirmModal
          title={t('blocklist.deleteRule')}
          description={t('blocklist.deleteConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => deleteMutation.mutate(deleteId)}
          onCancel={() => setDeleteId(null)}
          danger
        />
      )}
    </div>
  );
}
